import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import clsx from 'clsx';
import AppIcon from './AppIcon';
import { DOCS_GROUPS, DOCS_PATH, docPath, docsInGroup } from '@/data/docs/meta';
import type { DocSection } from '@/data/docs/types';

interface NavProps {
  activeSlug?: string;
  /** Разделы открытой статьи, вложенным списком под её пунктом. */
  sections?: DocSection[];
  sectionsClassName?: string;
  onNavigate?: () => void;
}

export function DocsNav({ activeSlug, sections, sectionsClassName, onNavigate }: NavProps) {
  return (
    <nav aria-label="Разделы документации" className="text-[14.5px]">
      {DOCS_GROUPS.map((group) => (
        <div key={group.id} className="mt-7 first:mt-0">
          <p className="px-2.5 text-[12.5px] font-medium text-ink/60">{group.title}</p>
          <ul className="mt-2 space-y-px">
            {docsInGroup(group.id).map((doc) => {
              const active = doc.slug === activeSlug;
              return (
                <li key={doc.slug}>
                  <Link
                    to={docPath(doc.slug)}
                    onClick={onNavigate}
                    aria-current={active ? 'page' : undefined}
                    className={clsx(
                      'flex items-center gap-2.5 rounded-lg px-2.5 py-[7px] leading-tight transition-colors',
                      active ? 'bg-ink/[0.06] font-medium text-ink' : 'text-ink/75 hover:bg-ink/[0.035] hover:text-ink',
                    )}
                  >
                    <AppIcon slug={doc.slug} size={20} />
                    {doc.title}
                  </Link>
                  {active && sections && sections.length > 0 ? (
                    <ul className={clsx('mb-2 ml-[21px] mt-1 border-l border-ink/10 pl-2.5', sectionsClassName)}>
                      {sections.map((section) => (
                        <li key={section.id}>
                          <Link
                            to={`#${section.id}`}
                            onClick={onNavigate}
                            className="block rounded-md px-2 py-1.5 text-[13.5px] leading-snug text-ink/65 transition-colors hover:text-ink"
                          >
                            {section.title}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
}

interface DrawerProps {
  activeSlug: string;
  sections: DocSection[];
  onClose: () => void;
}

/** Навигация на телефоне и планшете. Рендерится в body: backdrop-filter шапки ломает position: fixed у потомков. */
export function DocsNavDrawer({ activeSlug, sections, onClose }: DrawerProps) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return createPortal(
    <div role="dialog" aria-modal="true" aria-label="Разделы документации" className="fixed inset-0 z-[100] lg:hidden">
      <button
        type="button"
        tabIndex={-1}
        aria-label="Закрыть"
        onClick={onClose}
        className="absolute inset-0 bg-ink/30 motion-safe:animate-[docs-fade_200ms_ease-out]"
      />
      <div className="relative flex h-full w-[86%] max-w-[22rem] flex-col bg-white shadow-deep motion-safe:animate-[docs-drawer_280ms_cubic-bezier(0.16,1,0.3,1)]">
        <div className="flex h-16 shrink-0 items-center justify-between border-b border-ink/[0.08] pl-5 pr-3">
          <Link to={DOCS_PATH} onClick={onClose} className="text-[15px] font-medium text-ink">
            Документация
          </Link>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Закрыть разделы"
            className="grid h-10 w-10 place-items-center rounded-xl text-ink/70 transition-colors hover:bg-ink/5 hover:text-ink"
          >
            <X size={20} strokeWidth={1.8} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto overscroll-contain px-3 pb-10 pt-5">
          <DocsNav activeSlug={activeSlug} sections={sections} onNavigate={onClose} />
        </div>
      </div>
    </div>,
    document.body,
  );
}
